import React from 'react';

import { Trans } from '@lingui/macro';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Paper from '../../../misc/Paper';
import PaperHeader from '../../../misc/PaperHeader';

const directSrt = (value) => /^srt:\/\/[^/?#]+:\d+(?:\?[^#]*)?$/.test(value.trim());

export default function StrictTransport(props) {
	const pid = props.settings.videoPid;
	const pidValid = pid === '' || (/^(?:0x[0-9a-f]+|\d+)$/i.test(pid) && Number.parseInt(pid, 0) >= 32 && Number.parseInt(pid, 0) <= 8190);
	const complete = !props.settings.enabled || (props.settings.ingest?.srt_url && directSrt(props.settings.output) &&
		Number(props.settings.sourceFps) > 0 && Number(props.settings.sourceFps) <= 120 && pidValid);
	const change = (key) => (event) => props.onChange({
		...props.settings,
		[key]: key === 'enabled' ? event.target.checked : event.target.value,
	});

	return (
		<Paper xs={12} sm={9} md={6} marginBottom="6em" className="PaperM">
			<PaperHeader spacing={2} variant="h1" title={<Trans>Transport preservation</Trans>} onAbort={props.onAbort} />
			<Grid container spacing={2}>
				<Grid item xs={12}><Divider /></Grid>
				<Grid item xs={12}>
					<FormControlLabel control={<Switch checked={props.settings.enabled} onChange={change('enabled')} />}
						label={<Trans>Preserve original MPEG-TS transport</Trans>} />
				</Grid>
				{props.settings.enabled && <>
					<Grid item xs={12}>
						<Alert severity="warning">
							<Trans>This bypasses MediaMTX and the normal encoder path. Airchain creates a private SRT intake for you. Captions stop if PID, table, PCR, or bitrate preservation cannot be guaranteed.</Trans>
						</Alert>
					</Grid>
					<Grid item xs={12}>
						{props.settings.ingest?.srt_url ? <TextField fullWidth label={<Trans>1. Send your MPEG-TS video to this private SRT URL</Trans>}
							value={props.settings.ingest.srt_url} InputProps={{ readOnly: true }}
							helperText={<Trans>Use this exact URL in your encoder. It includes the required SRT encryption settings.</Trans>} /> :
							<Button variant="outlined" color="primary" onClick={props.onProvision} disabled={props.provisioning}>
								<Trans>Generate private SRT intake</Trans>
							</Button>}
					</Grid>
					<Grid item xs={12}>
						<TextField fullWidth required label={<Trans>2. Customer return SRT URL</Trans>} value={props.settings.output}
							onChange={change('output')} error={props.settings.output !== '' && !directSrt(props.settings.output)}
							helperText={<Trans>Configure your decoder or gateway as an SRT listener, then enter its URL. Airchain calls it with the captioned transport stream.</Trans>} />
					</Grid>
					<Grid item xs={6}>
						<TextField fullWidth label={<Trans>Video PID (optional)</Trans>} value={pid} onChange={change('videoPid')}
							error={!pidValid} helperText={<Trans>Decimal or 0xHEX; leave blank to detect one video PID.</Trans>} />
					</Grid>
					<Grid item xs={6}>
						<TextField fullWidth required type="number" label={<Trans>Source frame rate</Trans>} value={props.settings.sourceFps}
							onChange={change('sourceFps')} inputProps={{ min: 1, max: 120, step: 0.001 }} />
					</Grid>
					<Grid item xs={12}>
						<Typography variant="body2"><Trans>The source must reserve enough null packets for caption data. Existing PMT language descriptors are preserved and cannot be added here.</Trans></Typography>
					</Grid>
				</>}
				<Grid item xs={12}><Divider /></Grid>
				{props.showBack && <Grid item xs={3}><Button variant="outlined" color="default" fullWidth onClick={props.onBack}><Trans>Back</Trans></Button></Grid>}
				<Grid item xs={props.showBack ? 9 : 12}><Button variant="outlined" color="primary" fullWidth onClick={props.onNext} disabled={!complete}><Trans>Next</Trans></Button></Grid>
			</Grid>
		</Paper>
	);
}

StrictTransport.defaultProps = {
	onAbort: () => {}, onBack: () => {}, onNext: () => {}, onChange: () => {}, onProvision: () => {},
	provisioning: false,
	showBack: true,
	settings: { enabled: false, output: '', videoPid: '', sourceFps: '29.97', ingest: null },
};
